import * as THREE from 'three';
import { PLAYABLE_RADIUS, type Terrain } from '../world/Terrain';
import { clamp, damp } from '../world/noise';

export type CircleCollider = { x: number; z: number; r: number };

export const SPEEDS = { walk: 1.55, run: 4.3, sprint: 6.6, crouch: 1.35 };
const GRAVITY = 9.8;
const JUMP_SPEED = 4.7;
/** Second jump: a little softer than the first, with a push along the facing. */
const DOUBLE_JUMP_SPEED = 4.25;
const DOUBLE_JUMP_KICK = 1.7;
const COYOTE = 0.12;
/** Slide: a burst along the run, then friction bleeds it off. */
const SLIDE_MIN_SPEED = 3.2;
const SLIDE_BOOST = 2.4;
const SLIDE_MAX = 8.4;
const SLIDE_TIME = 0.95;
const SLIDE_FRICTION = 1.15;

const tmpN = new THREE.Vector3();

export class PlayerController {
  readonly position = new THREE.Vector3();
  readonly velocity = new THREE.Vector3();
  yaw = Math.PI;
  grounded = true;
  turnRate = 0;
  sprint = 0;
  walkMode = false;
  /** Seconds since leaving the ground (coyote time support). */
  private airTime = 0;
  private jumpBuffer = 0;
  /** Outward push factor near the world edge, exposed for UI hints. */
  edgeFactor = 0;
  landedImpact = 0;
  jumped = false;
  doubleJumped = false;
  /** Jumps used since leaving the ground: 0 grounded, 1 after the jump, 2 after the double jump. */
  jumps = 0;
  private airJumpReady = true;
  crouching = false;
  /** Seconds into the current slide, or -1. */
  slideTime = -1;
  slideStarted = false;
  private crouchWas = false;
  private slideCooldown = 0;
  private readonly slideDir = new THREE.Vector2();
  private readonly desired = new THREE.Vector3();

  constructor(private readonly terrain: Terrain) {}

  place(x: number, z: number, yaw: number): void {
    this.position.set(x, this.terrain.heightAt(x, z), z);
    this.velocity.set(0, 0, 0);
    this.yaw = yaw;
    this.grounded = true;
    this.jumps = 0;
    this.airJumpReady = true;
  }

  update(
    dt: number,
    move: THREE.Vector2,
    cameraYaw: number,
    sprintHeld: boolean,
    jumpPressed: boolean,
    colliders: CircleCollider[],
    locked: boolean,
    slashLocked = false,
    aim = false,
    crouchHeld = false,
  ): void {
    this.jumped = false;
    this.doubleJumped = false;
    this.slideStarted = false;
    this.slideCooldown = Math.max(0, this.slideCooldown - dt);
    const crouchEdge = crouchHeld && !this.crouchWas;
    this.crouchWas = crouchHeld;
    this.landedImpact = 0;
    const fx = Math.sin(cameraYaw);
    const fz = Math.cos(cameraYaw);
    const rx = -fz;
    const rz = fx;
    let dx = fx * move.y + rx * move.x;
    let dz = fz * move.y + rz * move.x;
    const mag = Math.min(1, Math.hypot(dx, dz));
    const aimYaw = Math.atan2(dx, dz);
    // A swing plants the feet but leaves the facing free.
    const moveLocked = locked;
    if (moveLocked) {
      dx = 0;
      dz = 0;
    }
    // A swing slows the feet but keeps them free.
    const inputMag = moveLocked ? 0 : slashLocked ? mag * 0.4 : mag;
    const wantsSprint = sprintHeld && inputMag > 0.5 && !this.walkMode;
    this.sprint = damp(this.sprint, wantsSprint ? 1 : 0, 4, dt);
    // Crouch in place or walk low; a crouch pressed at a run turns into a slide.
    const runSpeed = this.horizontalSpeed;
    if (crouchEdge && this.grounded && !locked && runSpeed > SLIDE_MIN_SPEED && this.slideTime < 0 && this.slideCooldown <= 0) {
      this.slideTime = 0;
      this.slideStarted = true;
      this.slideDir.set(this.velocity.x, this.velocity.z).normalize();
      const boosted = Math.min(runSpeed + SLIDE_BOOST, SLIDE_MAX);
      this.velocity.x = this.slideDir.x * boosted;
      this.velocity.z = this.slideDir.y * boosted;
    }
    this.crouching = crouchHeld && this.grounded && this.slideTime < 0;
    const base = this.crouching ? SPEEDS.crouch : this.walkMode ? SPEEDS.walk : SPEEDS.run;
    let targetSpeed = inputMag * (base + (SPEEDS.sprint - SPEEDS.run) * (this.crouching ? 0 : this.sprint));
    if (slashLocked) targetSpeed = Math.min(targetSpeed, SPEEDS.run * 0.4);

    // Uphill slows, downhill carries a little extra speed.
    if (inputMag > 0) {
      this.terrain.normalAt(this.position.x, this.position.z, tmpN);
      const slope = -(tmpN.x * dx + tmpN.z * dz) / Math.max(inputMag, 1e-3);
      targetSpeed *= clamp(1 - slope * 1.1, 0.72, 1.12);
    }

    const len = Math.hypot(dx, dz) || 1;
    this.desired.set((dx / len) * targetSpeed, 0, (dz / len) * targetSpeed);

    // Soft world boundary: the wind turns you back toward the fields.
    const r = Math.hypot(this.position.x, this.position.z);
    this.edgeFactor = clamp((r - (PLAYABLE_RADIUS - 40)) / 40, 0, 1);
    if (this.edgeFactor > 0) {
      const ox = this.position.x / r;
      const oz = this.position.z / r;
      const outward = this.desired.x * ox + this.desired.z * oz;
      if (outward > 0) {
        this.desired.x -= ox * outward * this.edgeFactor;
        this.desired.z -= oz * outward * this.edgeFactor;
      }
      this.desired.x -= ox * this.edgeFactor * 1.5;
      this.desired.z -= oz * this.edgeFactor * 1.5;
    }

    if (this.slideTime >= 0) {
      // Momentum carries the slide; the stick only steers it a little.
      this.slideTime += dt;
      const sp = this.horizontalSpeed * Math.exp(-SLIDE_FRICTION * dt);
      if (inputMag > 0.1) {
        const want = Math.atan2(dx, dz);
        const cur = Math.atan2(this.slideDir.x, this.slideDir.y);
        let dd = want - cur;
        dd = Math.atan2(Math.sin(dd), Math.cos(dd));
        const turned = cur + clamp(dd, -1.2 * dt, 1.2 * dt);
        this.slideDir.set(Math.sin(turned), Math.cos(turned));
      }
      this.velocity.x = this.slideDir.x * sp;
      this.velocity.z = this.slideDir.y * sp;
      if (sp < 2.2 || this.slideTime > SLIDE_TIME || !this.grounded) {
        this.slideTime = -1;
        this.slideCooldown = 0.25;
      }
    } else {
      const accel = this.grounded ? (targetSpeed > this.horizontalSpeed ? 7.5 : 9.5) : 2.2;
      this.velocity.x = damp(this.velocity.x, this.desired.x, accel * 0.45, dt);
      this.velocity.z = damp(this.velocity.z, this.desired.z, accel * 0.45, dt);
    }

    // Face the direction of travel.
    const speed = this.horizontalSpeed;
    const previousYaw = this.yaw;
    if (this.slideTime >= 0) {
      const targetYaw = Math.atan2(this.velocity.x, this.velocity.z);
      let delta = targetYaw - this.yaw;
      delta = Math.atan2(Math.sin(delta), Math.cos(delta));
      this.yaw += delta * Math.min(1, dt * 12);
    } else if (speed > 0.3 && inputMag > 0.05) {
      const targetYaw = Math.atan2(this.velocity.x, this.velocity.z);
      let delta = targetYaw - this.yaw;
      delta = Math.atan2(Math.sin(delta), Math.cos(delta));
      const maxTurn = (this.grounded ? 9 : 3) * dt;
      this.yaw += clamp(delta * Math.min(1, dt * 10), -maxTurn, maxTurn);
    } else if (slashLocked && aim && mag > 0.2) {
      let delta = aimYaw - this.yaw;
      delta = Math.atan2(Math.sin(delta), Math.cos(delta));
      this.yaw += clamp(delta * Math.min(1, dt * 16), -14 * dt, 14 * dt);
    }
    let yawDelta = this.yaw - previousYaw;
    yawDelta = Math.atan2(Math.sin(yawDelta), Math.cos(yawDelta));
    this.turnRate = damp(this.turnRate, yawDelta / Math.max(dt, 1e-4), 8, dt);

    // Jumping with a short buffer and coyote window; one extra jump per airtime once past it.
    const airborne = !this.grounded && this.airTime >= COYOTE;
    if (jumpPressed && !locked && !slashLocked && airborne && this.airJumpReady) {
      this.velocity.y = DOUBLE_JUMP_SPEED + Math.max(this.velocity.y, 0) * 0.25;
      this.velocity.x += Math.sin(this.yaw) * DOUBLE_JUMP_KICK;
      this.velocity.z += Math.cos(this.yaw) * DOUBLE_JUMP_KICK;
      const hs = this.horizontalSpeed;
      const cap = SPEEDS.sprint + 0.6;
      if (hs > cap) {
        this.velocity.x *= cap / hs;
        this.velocity.z *= cap / hs;
      }
      this.airJumpReady = false;
      this.doubleJumped = true;
      this.jumps = 2;
      this.jumpBuffer = 0;
    } else {
      this.jumpBuffer = jumpPressed && !locked ? 0.15 : Math.max(0, this.jumpBuffer - dt);
    }
    if (this.jumpBuffer > 0 && (this.grounded || this.airTime < COYOTE)) {
      this.velocity.y = JUMP_SPEED + Math.min(speed, 6) * 0.06;
      this.grounded = false;
      this.airTime = 0.2;
      this.jumpBuffer = 0;
      this.jumped = true;
      this.jumps = 1;
      // Jumping out of a slide keeps its speed.
      this.slideTime = -1;
    }

    if (!this.grounded) {
      const g = this.velocity.y < 0 ? GRAVITY * 1.45 : GRAVITY;
      this.velocity.y -= g * dt;
      this.airTime += dt;
    }

    this.position.x += this.velocity.x * dt;
    this.position.z += this.velocity.z * dt;
    this.position.y += this.velocity.y * dt;

    for (const c of colliders) {
      const cx = this.position.x - c.x;
      const cz = this.position.z - c.z;
      const d = Math.hypot(cx, cz);
      const min = c.r + 0.3;
      if (d < min && d > 1e-4) {
        this.position.x = c.x + (cx / d) * min;
        this.position.z = c.z + (cz / d) * min;
        const vn = (this.velocity.x * cx + this.velocity.z * cz) / d;
        if (vn < 0) {
          this.velocity.x -= (cx / d) * vn;
          this.velocity.z -= (cz / d) * vn;
        }
      }
    }

    const ground = this.terrain.heightAt(this.position.x, this.position.z);
    if (this.grounded) {
      if (this.position.y - ground > 0.35 && this.velocity.y <= 0) {
        this.grounded = false;
        this.airTime = 0;
      } else {
        this.position.y = ground;
        this.velocity.y = 0;
      }
    } else if (this.position.y <= ground && this.velocity.y <= 0) {
      this.landedImpact = -this.velocity.y;
      this.position.y = ground;
      this.velocity.y = 0;
      this.grounded = true;
      this.airTime = 0;
      this.jumps = 0;
      this.airJumpReady = true;
    }
  }

  get horizontalSpeed(): number {
    return Math.hypot(this.velocity.x, this.velocity.z);
  }
}
